import React from 'react';
import {useTranslation} from 'react-i18next';
import {isFieldDiff, isPlainObject} from '../shapes';
import {HIDDEN_FIELDS} from '../labels';
import {
    NESTED_DIFF_FIELD_ORDER,
    buildChangeCountTitle,
    extraFieldsSummary,
    orderDiffFields,
    resolveDiffCurrency,
} from '../diff_field_types';
import {DiffList, DiffStory} from '../primitives/DiffList';
import {DetailFooter} from '../primitives/DetailFooter';

function stringOrNull(value) {
    return typeof value === 'string' && value.trim() ? value.trim() : null;
}

// Qué se editó, en una línea: la cuenta publicitaria o el usuario. Devuelve también el campo
// consumido para no repetirlo en el pie.
function contextInfo(detail, t) {
    const name = stringOrNull(detail.account_name);
    if (name) {
        return {
            field: 'account_name',
            text: t('activity_log.detail.context_account', {name, interpolation: {escapeValue: false}}),
        };
    }
    const email = stringOrNull(detail.edited_email);
    if (email) {
        return {
            field: 'edited_email',
            text: t('activity_log.detail.context_user', {email, interpolation: {escapeValue: false}}),
        };
    }
    return null;
}

// Diff ANIDADO {contexto..., changes: {campo: {before, after}}} — AD_ACCOUNT_EDITED,
// USER_EDITED. El contexto (cuenta / usuario) va como línea introductoria y los cambios se
// leen igual que en un diff plano: moneda del propio diff (daily_limit_currency), frase
// cuando hay uno solo, lista cuando hay varios.
export function NestedDiffDetail({detail}) {
    const {t} = useTranslation();
    if (!isPlainObject(detail)) return null;

    const changes = isPlainObject(detail.changes) ? detail.changes : null;
    const context = contextInfo(detail, t);

    const nestedCurrency = resolveDiffCurrency(changes);
    const {currency, currencyBefore, currencyAfter} = nestedCurrency.currency
        ? nestedCurrency
        : resolveDiffCurrency(detail);
    const currencies = {currency, currencyBefore, currencyAfter};

    const fields = orderDiffFields(changes, NESTED_DIFF_FIELD_ORDER)
        .filter(field => !HIDDEN_FIELDS.has(field) && isFieldDiff(changes[field]));
    const extras = extraFieldsSummary(detail, t, ['changes', context ? context.field : null]);
    if (!context && !fields.length && !extras) return null;

    return (
        <React.Fragment>
            {context ? <div className="ald-story">{context.text}</div> : null}
            {fields.length === 1
                ? <DiffStory field={fields[0]} change={changes[fields[0]]} {...currencies}/>
                : <DiffList changes={changes} fieldOrder={fields} {...currencies}/>}
            <DetailFooter>{extras}</DetailFooter>
        </React.Fragment>
    );
}

// Título de la fila: description del backend + "· N cambios" (los cambios están anidados).
export function getNestedDiffTitle(entry, t) {
    const detail = entry ? entry.detail : null;
    return buildChangeCountTitle(entry, t, isPlainObject(detail) ? detail.changes : null);
}
